import type { Permission } from "@/core/auth/usePermissions";

/**
 * Verdict du gate RBAC du `CreatorOnboardingWizard`.
 *
 *   - "eligible" : l'user peut être poussé dans le wizard (détient
 *                  `creators.create`, ou superadmin god-mode).
 *   - "loading"  : permissions pas encore résolues → on attend (ne pas flasher
 *                  le wizard ni l'auto-open du panneau Créateurs avant de savoir
 *                  si l'user a le droit de créer un créateur).
 *   - "blocked"  : l'user n'a PAS `creators.create` (Chatter/VA/Operator) → le
 *                  wizard reste masqué et l'user atterrit sur le bureau.
 */
export type WizardRoleGate = "eligible" | "loading" | "blocked";

/**
 * Le wizard d'onboarding CRÉE un créateur (step identity → POST creator). Seuls
 * les rôles porteurs de `creators.create` (CEO/Manager) doivent y être poussés.
 *
 * Sans ce gate, TOUT membre d'une agence à 0 créateur basculait en mode
 * `no-creator` → auto-open forcé du panneau Créateurs + overlay wizard — alors
 * qu'un Chatter n'a même pas `creators.view` (panneau verrouillé/vide) ni le
 * droit de créer. Bug rapporté le 2026-06-18 : un chatter se connecte, atterrit
 * sur Créateur (invisible pour lui) au lieu du bureau, et à chaque réouverture
 * il y retombe (boucle, car `dismissedNoCreatorWizard` est memory-only).
 *
 * Distinct du garde `hasAnyCreator` de `useWizardData` (proprio multi-org dont
 * la 1re org est vide mais qui a des créateurs ailleurs) : ce gate-ci porte sur
 * le RÔLE, pas sur le nombre de créateurs.
 *
 * Le superadmin Dozka bypasse (god-mode cross-org) — il est déjà filtré en
 * amont dans `useWizardData`, ce paramètre est une ceinture-bretelles.
 */
export function wizardRoleGate(
  isSuperadmin: boolean,
  permissions: ReadonlySet<Permission> | null,
): WizardRoleGate {
  if (isSuperadmin) return "eligible";
  if (permissions === null) return "loading";
  return permissions.has("creators.create") ? "eligible" : "blocked";
}
